import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Beaker,
  BookOpenCheck,
  CheckCircle2,
  ExternalLink,
  FileSearch,
  FlaskConical,
  Info,
  Microscope,
} from "lucide-react";

import type {
  BioanalysisPhaseOne as BioanalysisPhaseOneModel,
  BioanalysisReadiness,
} from "../lib/bioanalysisPhaseOne";
import { cn } from "../lib/utils";

type BioanalysisTab = "analytes" | "methods" | "validation";

interface BioanalysisPhaseOneProps {
  analysis: BioanalysisPhaseOneModel;
}

const READINESS_LABELS: Record<BioanalysisReadiness, { label: string; className: string }> = {
  ready: { label: "可进入全文评估", className: "text-success border-success/30 bg-success/5" },
  partial: { label: "需补充关键证据", className: "text-warning border-warning/30 bg-warning/5" },
  insufficient: { label: "证据不足", className: "text-destructive border-destructive/30 bg-destructive/5" },
};

function EvidenceLink({ href, label }: { href: string; label: string }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
    >
      {label}
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </a>
  );
}

function EmptyValue() {
  return <span className="text-muted-foreground">待全文确认</span>;
}

function AnalytesTable({ analysis }: BioanalysisPhaseOneProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table aria-label="生物分析对象识别表" className="min-w-[48rem] w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
            <th className="w-16 px-3 py-2.5 text-left font-medium">序号</th>
            <th className="w-48 px-3 py-2.5 text-left font-medium">分析物</th>
            <th className="w-44 px-3 py-2.5 text-left font-medium">英文名 / INN</th>
            <th className="w-40 px-3 py-2.5 text-left font-medium">建议角色</th>
            <th className="px-3 py-2.5 text-left font-medium">身份依据</th>
            <th className="w-28 px-3 py-2.5 text-left font-medium">状态</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {analysis.analytes.map((analyte, index) => (
            <tr key={analyte.id} className="align-top hover:bg-muted/20">
              <td className="px-3 py-3 tabular-nums text-muted-foreground">{index + 1}</td>
              <td className="px-3 py-3 font-semibold text-foreground">{analyte.nameCn}</td>
              <td className="px-3 py-3 text-foreground">{analyte.nameEn}</td>
              <td className="px-3 py-3 text-foreground">{analyte.role}</td>
              <td className="px-3 py-3 leading-relaxed text-muted-foreground">{analyte.evidence}</td>
              <td className="px-3 py-3">
                <span className={cn(
                  "inline-flex rounded-md px-2 py-1 text-xs font-semibold",
                  analyte.status === "confirmed"
                    ? "bg-success/10 text-success"
                    : "bg-warning/10 text-warning",
                )}>
                  {analyte.status === "confirmed" ? "已确认" : "需核对"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MethodsTable({ analysis }: BioanalysisPhaseOneProps) {
  if (analysis.methodEvidence.length === 0) {
    return (
      <div className="border border-dashed border-border bg-muted/10 px-4 py-5 text-sm text-muted-foreground">
        当前 PubMed 返回结果中尚未识别到明确报告检测平台、基质或方法验证信息的文献。系统不会用常见经验值补齐空白。
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table aria-label="生物分析方法证据表" className="min-w-[82rem] w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-muted-foreground">
            <th className="w-12 px-3 py-2.5 text-left font-medium">序号</th>
            <th className="min-w-[24rem] px-3 py-2.5 text-left font-medium">文献与来源</th>
            <th className="w-32 px-3 py-2.5 text-left font-medium">基质</th>
            <th className="w-32 px-3 py-2.5 text-left font-medium">检测平台</th>
            <th className="w-36 px-3 py-2.5 text-left font-medium">样本前处理</th>
            <th className="w-28 px-3 py-2.5 text-left font-medium">样本体积</th>
            <th className="w-28 px-3 py-2.5 text-left font-medium">LLOQ</th>
            <th className="w-40 px-3 py-2.5 text-left font-medium">校准范围</th>
            <th className="w-28 px-3 py-2.5 text-left font-medium">运行时间</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {analysis.methodEvidence.map((item, index) => (
            <tr key={item.id} className="align-top hover:bg-muted/20">
              <td className="px-3 py-3 tabular-nums text-muted-foreground">{index + 1}</td>
              <td className="px-3 py-3">
                <div className="font-semibold leading-relaxed text-foreground">{item.title}</div>
                <div className="mt-1 text-muted-foreground">
                  {[item.journal, item.year, item.pmid ? `PMID ${item.pmid}` : ""].filter(Boolean).join(" · ")}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <EvidenceLink href={item.url} label="PubMed" />
                  <EvidenceLink href={item.doiUrl} label="DOI" />
                  <EvidenceLink href={item.fullTextUrl} label="原文" />
                </div>
              </td>
              <td className="px-3 py-3 text-foreground">{item.matrix.join("、") || <EmptyValue />}</td>
              <td className="px-3 py-3 font-medium text-foreground">{item.platform || <EmptyValue />}</td>
              <td className="px-3 py-3 text-foreground">{item.preparation.join("、") || <EmptyValue />}</td>
              <td className="px-3 py-3 tabular-nums text-foreground">{item.sampleVolume || <EmptyValue />}</td>
              <td className="px-3 py-3 tabular-nums text-foreground">{item.lloq || <EmptyValue />}</td>
              <td className="px-3 py-3 tabular-nums text-foreground">{item.calibrationRange || <EmptyValue />}</td>
              <td className="px-3 py-3 tabular-nums text-foreground">{item.runTime || <EmptyValue />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ValidationTable({ analysis }: BioanalysisPhaseOneProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table aria-label="ICH M10 生物分析方法验证核查表" className="min-w-[68rem] w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
            <th className="w-16 px-3 py-2.5 text-left font-medium">序号</th>
            <th className="w-52 px-3 py-2.5 text-left font-medium">验证项目</th>
            <th className="w-24 px-3 py-2.5 text-left font-medium">适用性</th>
            <th className="min-w-[20rem] px-3 py-2.5 text-left font-medium">专业目的</th>
            <th className="min-w-[24rem] px-3 py-2.5 text-left font-medium">应准备的证据</th>
            <th className="w-40 px-3 py-2.5 text-left font-medium">法规依据</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {analysis.validationChecklist.map((item, index) => (
            <tr key={item.id} className="align-top hover:bg-muted/20">
              <td className="px-3 py-3 tabular-nums text-muted-foreground">{index + 1}</td>
              <td className="px-3 py-3 font-semibold text-foreground">{item.item}</td>
              <td className="px-3 py-3">
                <span className={cn(
                  "inline-flex rounded-md px-2 py-1 text-xs font-semibold",
                  item.applicability === "required"
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground",
                )}>
                  {item.applicability === "required" ? "必需" : "条件适用"}
                </span>
              </td>
              <td className="px-3 py-3 leading-relaxed text-foreground">{item.objective}</td>
              <td className="px-3 py-3 leading-relaxed text-muted-foreground">{item.evidenceRequired}</td>
              <td className="px-3 py-3">
                <EvidenceLink href={item.sourceUrl} label={item.sourceTitle} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BioanalysisPhaseOne({ analysis }: BioanalysisPhaseOneProps) {
  const [activeTab, setActiveTab] = useState<BioanalysisTab>("analytes");
  const readiness = READINESS_LABELS[analysis.readiness];
  const tabs = useMemo(() => [
    { id: "analytes" as const, label: "分析对象", count: analysis.analytes.length, icon: Microscope },
    { id: "methods" as const, label: "方法文献", count: analysis.methodEvidence.length, icon: FileSearch },
    { id: "validation" as const, label: "ICH M10 核查", count: analysis.validationChecklist.length, icon: BookOpenCheck },
  ], [analysis.analytes.length, analysis.methodEvidence.length, analysis.validationChecklist.length]);

  return (
    <section className="card overflow-hidden" aria-labelledby="bioanalysis-phase-one-title">
      <div className="border-b border-border px-5 py-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-primary" aria-hidden="true" />
              <h2 id="bioanalysis-phase-one-title" className="text-base font-semibold text-foreground">
                生物样本分析策略 · 第一阶段
              </h2>
            </div>
            <p className="mt-1.5 max-w-4xl text-sm leading-relaxed text-muted-foreground">
              {analysis.summary}
            </p>
          </div>
          <div className={cn("flex flex-shrink-0 items-center gap-2 rounded-md border px-3 py-2", readiness.className)}>
            {analysis.readiness === "ready" ? (
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            ) : (
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            )}
            <span className="text-sm font-semibold">{readiness.label}</span>
            <span className="border-l border-current/20 pl-2 text-sm font-bold tabular-nums">{analysis.score}/100</span>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 sm:px-6">
        <div className="inline-flex max-w-full overflow-x-auto rounded-md border border-border bg-muted/30 p-1" role="tablist" aria-label="生物样本分析视图">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "inline-flex min-h-9 items-center gap-2 whitespace-nowrap rounded px-3 text-sm font-medium transition-colors",
                  selected ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {tab.label}
                <span className="rounded bg-muted px-1.5 py-0.5 text-[0.68rem] tabular-nums">{tab.count}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-4" role="tabpanel">
          {activeTab === "analytes" && <AnalytesTable analysis={analysis} />}
          {activeTab === "methods" && <MethodsTable analysis={analysis} />}
          {activeTab === "validation" && <ValidationTable analysis={analysis} />}
        </div>

        {analysis.gaps.length > 0 && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Beaker className="h-4 w-4 text-warning" aria-hidden="true" />
              进入方法开发前需要补齐
            </div>
            <ul className="mt-2 grid gap-2 text-sm leading-relaxed text-muted-foreground md:grid-cols-2">
              {analysis.gaps.map((gap) => (
                <li key={gap} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-warning" aria-hidden="true" />
                  <span>{gap}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex items-start gap-2 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{analysis.limitations.join(" ")}</span>
        </div>
      </div>
    </section>
  );
}

